// src/features/executive/overview/OverviewTab.tsx
// Cross-tab landing page: the hero row of every executive tab the user may
// open, stacked in tab order. Every widget here is the same component the
// destination tab renders and reads the shared filters from redux, so the
// numbers on this page always match the tab they came from.
import { useTranslation } from 'react-i18next'
import SectionHeader from 'features/executive/components/SectionHeader/SectionHeader'
import { useExecutivePermissions } from 'features/executive/permission-store'
import { getOverviewSections, OverviewSectionKey } from 'features/executive/executive-nav.utils'
import RevenueSummaryTile from 'features/executive/performance/widgets/RevenueSummaryTile'
import AvgBillTile from 'features/executive/performance/widgets/AvgBillTile'
import AvgRevenueTile from 'features/executive/performance/widgets/AvgRevenueTile'
import PatientSummaryCard from 'features/executive/performance/widgets/PatientSummaryCard'
import FinancialSummaryCard from 'features/executive/account/widgets/FinancialSummaryCard'
import SalesSummaryTiles from 'features/executive/sales/widgets/SalesSummaryTiles'
import NewVsExistingDonut from 'features/executive/insight/widgets/NewVsExistingDonut'
import RetentionRateTile from 'features/executive/insight/widgets/RetentionRateTile'
import { HeroRow, Row, Section, TabRoot } from './usedStyle'

const SECTION_BODY: Record<OverviewSectionKey, () => JSX.Element> = {
  performance: () => (
    <HeroRow>
      <RevenueSummaryTile />
      <AvgBillTile />
      <AvgRevenueTile />
      <PatientSummaryCard />
    </HeroRow>
  ),
  account: () => <FinancialSummaryCard />,
  sales: () => (
    <HeroRow>
      <SalesSummaryTiles />
    </HeroRow>
  ),
  insight: () => (
    <Row>
      <NewVsExistingDonut />
      <RetentionRateTile />
    </Row>
  ),
}

export default function OverviewTab() {
  const { t } = useTranslation()
  const tabs = useExecutivePermissions().executiveDashboardTabs
  const sections = getOverviewSections(tabs)

  return (
    <TabRoot>
      {sections.map((key) => {
        const Body = SECTION_BODY[key]
        return (
          <Section key={key}>
            <SectionHeader
              label={t(`EXECUTIVE.TAB.${key.toUpperCase()}`)}
              caption={t(`EXECUTIVE.OVERVIEW.SECTION_CAPTION.${key.toUpperCase()}`)}
            />
            <Body />
          </Section>
        )
      })}
    </TabRoot>
  )
}
